/**
 * Drawer for Needs Verification row actions. Content varies by action.
 * Form fields for Manually Enter Benefits align to Medplum CoverageEligibilityResponse
 * (copay, coinsurance, deductible — see https://www.medplum.com/docs/integration/stedi/eligibility-checks).
 */

import {
  Button,
  Checkbox,
  Drawer,
  Group,
  NumberInput,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import type { VerificationErrorRow } from '../types/lifecycle';

function formatDate(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const ACTION_TITLES: Record<string, string> = {
  'View Error Details': 'Error details',
  'Retry Sohar': 'Retry Sohar verification',
  'Manually Enter Benefits': 'Manually enter benefits',
  'Confirm Assumed Estimate': 'Confirm assumed estimate',
  'Correct and Notify Client': 'Correct benefits and notify client',
  'View Coverage': 'Coverage details',
};

export interface VerificationActionDrawerProps {
  open: boolean;
  onClose: () => void;
  action: string | null;
  row: VerificationErrorRow | null;
  onConfirm?: (action: string, payload?: ManualBenefitsPayload) => void;
}

/** Benefits form payload — maps to CoverageEligibilityResponse benefit items (copay, coinsurance, deductible). */
export interface ManualBenefitsPayload {
  copay?: number;
  coinsurancePercent?: number;
  deductible?: number;
  deductibleRemaining?: number;
  sessionLimit?: number;
  notes?: string;
  notifyClient?: boolean;
}

export function VerificationActionDrawer({
  open,
  onClose,
  action,
  row,
  onConfirm,
}: VerificationActionDrawerProps): JSX.Element {
  const [memberId, setMemberId] = useState(row?.memberId ?? '');
  const [copay, setCopay] = useState<string>('');

  useEffect(() => {
    if (row) setMemberId(row.memberId ?? '');
  }, [row?.id]);
  const [coinsurancePercent, setCoinsurancePercent] = useState<string>('');
  const [deductible, setDeductible] = useState<string>('');
  const [deductibleRemaining, setDeductibleRemaining] = useState<string>('');
  const [sessionLimit, setSessionLimit] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [notifyClient, setNotifyClient] = useState(false);

  const title = action ? ACTION_TITLES[action] ?? action : '';
  const isFormAction = action === 'Manually Enter Benefits' || action === 'Correct and Notify Client';
  const isConfirmAction = action === 'Confirm Assumed Estimate';
  const isRetryAction = action === 'Retry Sohar';

  const handleSubmit = (): void => {
    if (isFormAction) {
      onConfirm?.(action!, {
        copay: copay ? Number(copay) : undefined,
        coinsurancePercent: coinsurancePercent ? Number(coinsurancePercent) : undefined,
        deductible: deductible ? Number(deductible) : undefined,
        deductibleRemaining: deductibleRemaining ? Number(deductibleRemaining) : undefined,
        sessionLimit: sessionLimit ? Number(sessionLimit) : undefined,
        notes: notes || undefined,
        notifyClient: action === 'Correct and Notify Client' ? notifyClient : undefined,
      });
    } else if (isConfirmAction) {
      onConfirm?.(action!);
    } else if (isRetryAction) {
      onConfirm?.(action!, { notes: memberId ? `Member ID: ${memberId}` : undefined });
    }
    onClose();
  };

  if (!row) return <></>;

  return (
    <Drawer opened={open} onClose={onClose} position="right" size="md" title={title}>
      {action === 'View Error Details' && (
        <Stack gap="sm">
          <Text size="sm"><strong>Client</strong> {row.clientName}</Text>
          <Text size="sm"><strong>Service type code</strong> {row.serviceTypeCodes?.join(', ') ?? '—'}</Text>
          <Text size="sm"><strong>Payer</strong> {row.payer}</Text>
          <Text size="sm"><strong>Member ID</strong> {row.memberId}</Text>
          <Text size="sm"><strong>Sohar error</strong> {row.soharErrorReason}</Text>
          <Text size="sm"><strong>Assumed copay</strong> {row.assumedCopay ?? '—'}</Text>
          <Text size="sm"><strong>Next appointment</strong> {row.soonestBookedAppointment ? formatDate(row.soonestBookedAppointment) : '—'}</Text>
          <Text size="sm"><strong>Days until appointment</strong> {row.daysUntilAppointment ?? '—'}</Text>
          <Button mt="md" variant="light" onClick={onClose}>Close</Button>
        </Stack>
      )}

      {action === 'View Coverage' && (
        <Stack gap="sm">
          <Text size="sm"><strong>Payer</strong> {row.payer}</Text>
          <Text size="sm"><strong>Member ID</strong> {row.memberId}</Text>
          <Text size="sm"><strong>Service type code</strong> {row.serviceTypeCodes?.join(', ') ?? '—'}</Text>
          <Text size="sm"><strong>Status</strong> Error (needs verification)</Text>
          <Text size="sm"><strong>Assumed copay</strong> {row.assumedCopay ?? '—'}</Text>
          <Button mt="md" variant="light" onClick={onClose}>Close</Button>
        </Stack>
      )}

      {isRetryAction && (
        <Stack gap="md">
          <Text size="sm" c="dimmed">Correct member ID or other details if needed, then retry the Sohar eligibility check.</Text>
          <TextInput label="Member ID" value={memberId} onChange={(e) => setMemberId(e.currentTarget.value)} placeholder="e.g. WXY123456" />
          <Group mt="md">
            <Button onClick={handleSubmit}>Retry Sohar</Button>
            <Button variant="subtle" onClick={onClose}>Cancel</Button>
          </Group>
        </Stack>
      )}

      {isFormAction && (
        <Stack gap="md">
          <Text size="xs" c="dimmed">Values map to CoverageEligibilityResponse benefit items (copay, coinsurance, deductible) for storage in Medplum.</Text>
          <NumberInput label="Copay ($)" min={0} decimalScale={2} value={copay} onChange={(v) => setCopay(String(v ?? ''))} placeholder="e.g. 30" />
          <NumberInput label="Coinsurance (%)" min={0} max={100} value={coinsurancePercent} onChange={(v) => setCoinsurancePercent(String(v ?? ''))} placeholder="e.g. 10" />
          <NumberInput label="Deductible ($)" min={0} decimalScale={2} value={deductible} onChange={(v) => setDeductible(String(v ?? ''))} placeholder="e.g. 500" />
          <NumberInput label="Deductible remaining ($)" min={0} decimalScale={2} value={deductibleRemaining} onChange={(v) => setDeductibleRemaining(String(v ?? ''))} placeholder="e.g. 250" />
          <NumberInput label="Session limit (optional)" min={0} value={sessionLimit} onChange={(v) => setSessionLimit(String(v ?? ''))} placeholder="e.g. 26" />
          <TextInput label="Notes" value={notes} onChange={(e) => setNotes(e.currentTarget.value)} placeholder="Admin notes" />
          {action === 'Correct and Notify Client' && (
            <Checkbox label="Notify client of corrected benefits" checked={notifyClient} onChange={(e) => setNotifyClient(e.currentTarget.checked)} />
          )}
          <Group mt="md">
            <Button onClick={handleSubmit}>Submit</Button>
            <Button variant="subtle" onClick={onClose}>Cancel</Button>
          </Group>
        </Stack>
      )}

      {isConfirmAction && (
        <Stack gap="md">
          <Text size="sm">Mark this verification as resolved using the assumed estimate ({row.assumedCopay ?? '—'})? This will clear the Needs Verification flag.</Text>
          <Group mt="md">
            <Button onClick={handleSubmit}>Confirm</Button>
            <Button variant="subtle" onClick={onClose}>Cancel</Button>
          </Group>
        </Stack>
      )}
    </Drawer>
  );
}
